<template>
  <templates-contentful-content
    v-if="post"
    v-bind="propsData"
    :template="template"
    :llm-type="vertical"
    :data-testid="dataTestid"
  >
    <template
      v-if="template.includes('Article3Column')"
      #hero
    >
      <V2MoleculesVerticalFormWidgetHero
        v-if="template === 'Article3Column'"
        :vertical="vertical"
        :is-income-protection="isIncomeProtection"
        :is-ovhc="isOvhc"
      />

      <MoleculesVerticalPostHero
        v-else-if="['Article3Column-D1', 'Article3Column-D2', 'Article3Column-D3'].includes(template)"
        :variant="template"
      />
    </template>

    <template #main-content>
      <RichTextRenderer
        :document="post.mainContent.json"
        :node-renderers="renderNodes()"
        :mark-renderers="RENDER_MARKS"
      />

      <template v-if="template === 'ArticleWithSidebarV2' || template.includes('Article3Column')">
        <AtomsAuthorBlock
          v-if="template.includes('Article3Column')"
          theme="expanded"
          :name="post?.author?.name"
          :image="post?.author?.image"
          :description="post?.author?.profile"
          :dimensions="{
            width: 200,
            height: 200
          }"
          :job-title="post?.author?.jobTitle"
          :vertical="post?.author?.vertical"
          :tips="post?.author?.tips"
          :url="authorUrl"
        />

        <hr
          class="mt-8 lg:mt-[2.9375rem] mb-6 lg:mb-10 border-b border-b-gray-400 border-t-0 border-l-0 border-r-0"
        />

        <MoleculesShareButtons
          title="Share this article"
          :buttons="[
            {
              name: 'Facebook',
              url: `https://www.facebook.com/sharer/sharer.php?u=${articleLink}`,
            },
            {
              name: 'Twitter',
              url: `https://twitter.com/share?text=${post.title}&url=${articleLink}`,
            },
            {
              name: 'Linkedin',
              url: `https://www.linkedin.com/shareArticle?mini=true&url=${articleLink}`,
            },
            {
              name: 'Whatsapp',
              url: `https://api.whatsapp.com/send?text=${articleLink}`,
            },
            {
              name: 'Copy',
              text: 'Copy',
              url: articleLink,
            },
          ]"
          image-lazy-load
          :class="{
            'pb-6 md:pb-10': template === 'ArticleWithSidebarV2'
          }"
        />
      </template>
    </template>

    <template
      v-if="template === 'ArticleLightWithSidebar'"
      #main-content-extra
    >
      <div class="px-4 lg:px-0">
        <div class="max-w-5.5xl mx-auto text-center pt-8 pb-16 border-t border-t-gray-300">
          <p class="text-2xl md:text-3.5xl font-bold mb-2">
            Compare today and discover what you can save!
          </p>

          <p class="text-base md:text-lg mb-6">
            Discover the best options for you
          </p>

          <atoms-cta
            :to="verticalData.formPage"
            :full="false"
            align="center"
            theme="primary"
          >
            Get started
          </atoms-cta>
        </div>
      </div>
    </template>

    <template
      v-if="template.includes('Article3Column') && devices.breakpoint('lg')"
      #sidebar-left
    >
      <atoms-headline
        tag="p"
        class="font-bold text-lg !py-0 mb-4"
      >
        In this guide
      </atoms-headline>

      <ul class="flex flex-col gap-4">
        <li
          v-for="(heading, headingKey) in jumpLinks"
          :key="headingKey"
          class="hover:underline hover:font-bold cursor-pointer text-sm"
          @click="handleScrollToJumplink(heading)"
        >
          {{ heading }}
        </li>
      </ul>
    </template>

    <template
      v-if="
        ['ArticleWithSidebarV2', 'ArticleLightWithSidebar'].includes(template) ||
          (template.includes('Article3Column') && devices.breakpoint('lg'))
      "
      #sidebar-right
    >
      <V2OrganismContentWidgetsSidebarV2
        v-if="template === 'ArticleWithSidebarV2'"
        :is-income-protection="isIncomeProtection"
        :vertical="vertical"
      />

      <div
        v-if="template.includes('Article3Column') && devices.breakpoint('lg')"
        class="flex flex-col"
      >
        <atoms-headline
          tag="p"
          class="font-bold text-lg !py-0 mb-4"
        >
          Related Articles
        </atoms-headline>

        <atoms-accordion
          v-for="(item, itemKey) in sidebarRelatedArticles"
          :key="itemKey"
          icon-type="arrow"
          size="medium"
          class="!text-base !border-gray-400"
        >
          <template #title>
            {{ item.title }}
          </template>

          <template #content>
            <div class="flex flex-col gap-4 text-sm">
              <nuxt-link
                v-for="(link, linkKey) in item.links"
                :key="linkKey"
                :to="link.url"
                target="_blank"
                class="hover:font-bold hover:underline"
                no-rel
              >
                {{ link.text }}
              </nuxt-link>
            </div>
          </template>
        </atoms-accordion>
      </div>

      <V2OrganismContentWidgetsSidebarV1
        v-if="['ArticleLightWithSidebar'].includes(template)"
        :vertical="vertical"
      />
    </template>

    <template
      v-if="template.includes('Article3Column') && devices.breakpoint('lg')"
      #sidebar-right-extra
    >
      <MoleculesTrustpilotCompactReview class="mt-10 border-1 border-gray-400" />
    </template>

    <template
      v-if="
        ['ArticleWithSidebarV2', 'ArticleLightWithSidebar'].includes(template) ||
          (template === 'Article3Column' && devices.breakpoint('lg') && !userLoggedIn)
      "
      #sidebar-right-join-club-plus
    >
      <MoleculesJoinClubPlus class="border-1 border-gray-400" />
    </template>

    <template
      v-if="
        template ||
          (
            !_isEmpty(DISCLAIMER?.[vertical]) &&
            (
              (template === 'ArticleWithSidebarV2' && vertical === 'Energy') ||
              ['ArticleLightWithSidebar'].includes(template)
            )
          )
      "
      #footer
    >
      <div
        v-if="template === 'ArticleLightWithSidebar'"
        :class="layoutMaxWidth"
      >
        <div
          v-if="guidesConfigData?.articleEnablePartnerSlider"
          class="px-4 lg:px-0"
        >
          <MoleculesCarouselPartner
            class="max-w-wrapper-md mx-auto text-center pt-8 pb-16"
            :vertical="vertical"
          />
        </div>

        <div
          v-if="guidesConfigData?.articleEnableCardCta"
          class="px-4 lg:px-0"
        >
          <div class="max-w-5.5xl mx-auto text-center pt-8 pb-16 border-t border-t-gray-300">
            <p class="text-3.5xl mb-4">
              Get Life Insurance quotes some of Australia’s Top Providers
            </p>

            <p class="text-lg mb-6">
              Compare some of Australia’s top life insurers with one simple form.
            </p>

            <V2AtomsButton :to="verticalData.formPage">
              Get started
            </V2AtomsButton>
          </div>
        </div>
      </div>

      <AtomsAuthorBlock
        v-if="!template.includes('Article3Column')"
        theme="compact"
        :name="post?.author?.name"
        :image="post?.author?.image"
        :description="post?.author?.profile"
        :dimensions="{
          width: 200,
          height: 200
        }"
        :job-title="post?.author?.jobTitle"
        :vertical="post?.author?.vertical"
        :tips="post?.author?.tips"
        :url="authorUrl"
      />

      <div
        v-if="!template.includes('ArticleLightWithSidebar') && !_isEmpty(relatedNewsItems?.items)"
        class="!py-16 !bg-neutral-50"
      >
        <div class="w-full max-w-wrapper-lg mx-auto px-4 md:px-8 flex flex-col items-end">
          <V2MoleculesPostTileGroup
            id="related-news"
            title="Related articles"
            title-size="large"
            title-class="mb-6 text-xl lg:text-2xl"
            tile-class="gap-y-4 lg:gap-x-[4.125rem]"
            :items="relatedNewsItems?.items"
            :items-per-row="3"
            image-lazy-load
          />

          <V2AtomsButton
            v-if="!_isEmpty(relatedNewsItems)"
            size="large"
            theme="secondary"
            class="!ml-auto mt-6 lg:mt-[4.25rem] w-full lg:w-auto justify-center leading-6 lg:bottom-4 relative"
            :full="false"
            :to="verticalData.guidesUrl"
            has-icon-right
          >
            View more
          </V2AtomsButton>
        </div>
      </div>

      <div
        v-if="
          !_isEmpty(DISCLAIMER?.[vertical]) &&
            (
              (template === 'ArticleWithSidebarV2' && vertical === 'Energy') ||
              ['ArticleLightWithSidebar'].includes(template)
            )
        "
        class="!py-16 !bg-neutral-50"
      >
        <molecules-home-footer-info
          theme="white-boxed"
          class="!py-0 !bg-transparent"
          :class="layoutMaxWidth"
        >
          <template #title>
            Things you should know
          </template>
          <template #description>
            <!-- eslint-disable vue/no-v-html -->
            <div v-html="DISCLAIMER[vertical]"></div>
          </template>
        </molecules-home-footer-info>
      </div>
    </template>
  </templates-contentful-content>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

import RichTextRenderer from 'contentful-rich-text-vue-renderer'

// variables
import { GUIDES_CONFIG } from '~/helpers/constants'

import {
  PAGE_TYPES,
  TEMPLATES
} from '~/constants/pages/contentful.js'

import {
  DISCLAIMER,
  RELATED_ARTICLES,
  DEFAULT_AUTHOR_URL,
  DEFAULT_LOAD_MORE_PER_PAGE
} from '~/constants/pages/contentful'

import { POST_TYPES } from '~/constants/verticals.constants'

import {
  RENDER_NODES,
  RENDER_MARKS,
  GET_FEATURE_IMAGE
} from '~/helpers/contentful'

defineOptions({
  name: 'TemplatesContentfulGuides'
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: 'contentful-guides'
  },

  isPreview: {
    type: Boolean,
    default: false
  }
})

const {
  internalDomainForInlinesHyperlink
} = useEnv()

const $route = useRoute()
const layoutsStore = useLayoutsStore()
const guidesStore = useGuidesStore()
const { devices } = deviceChecker()
const userStore = useUserStore()
const userLoggedIn = computed(() => userStore.isUserLoggedIn)

if (!$route.params.slug) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
    fatal: true
  })
}

const tempVertical = (() => {
  const temp = $route.params?.vertical || ''

  if (!temp) {
    const path = $route.path
    const pathArr = path.split('/')
      .filter(item => item)

    return pathArr?.[0]
  }

  return temp
})()

const slug = `/${tempVertical}/${$route.params.slug}`
const vertical = tempVertical && strToCapitalize(tempVertical.replace(/-/g, ' '))

const articleLink = import.meta.client ? window.location.href : ''

const post = await useAsyncData(`article-${slug}`, () => guidesStore.getArticle({
  vertical,
  slug,
  preview: props.isPreview
})).then(res => {
  if (!_isEmpty(res?.error?.value)) {
    throw createError(res.error.value)
  }

  if (!res?.data.value?.exist) {
    throw createError({
      statusCode: 404,
      statusMessage: 'POST DOES NOT EXIST',
      fatal: true
    })
  }

  return res?.data.value
})

if (post?.redirectUrl?.slug) {
  await navigateTo(`${post?.redirectUrl?.slug}/`, {
    redirectCode: 301
  })
}

const template = TEMPLATES.includes(post?.template)
  ? post?.template
  : 'ArticleWithSidebarV2'

const jumpLinks = computed(() => {
  return post?.mainContent.json.content
    .filter(item => item.nodeType === 'heading-2')
    .map(item => {
      const value = item?.content?.length > 1
        ? item.content.map(node => node?.value ?? '').join('')
        : item?.content?.[0]?.value ?? ''

      return value.replace(/[/\n]/g, '').trim()
    })
})

onMounted(async () => {
  await nextTick()

  // adds id to jumplinks
  const headingEls = document.querySelectorAll('#article-main-content h2')
  const jumpLinkSet = new Set(jumpLinks.value)

  headingEls.forEach(el => {
    const text = removeHtmlString(el.innerText.trim())

    if (!jumpLinkSet.has(text)) {
      return
    }

    const id = strToKebabCase(text)

    // idempotent: skip if already set (avoids redundant DOM mutation on HMR re-runs)
    if (el.id !== id) {
      el.setAttribute('id', id)
    }
  })
})

onBeforeMount(() => {
  layoutsStore.navigation.ctaOnly = template === 'ArticleLightWithSidebar'
})

onUnmounted(() => {
  layoutsStore.navigation.ctaOnly = false
})

const relatedArticles = await (async () => {
  if (
    !post?.exist ||
    !(template === 'ArticleWithSidebarV2' || template.includes('Article3Column'))
  ) {
    return []
  }

  const limit = DEFAULT_LOAD_MORE_PER_PAGE
  const { data } = await useAsyncData(`related-articles-${slug}`, () => guidesStore.fetchRelatedArticles(
    vertical,
    slug,
    false,
    limit
  ))
    .catch(err => {
      // eslint-disable-next-line no-console
      console.error(err)

      return { data: ref(null) }
    })

  return data.value?.total > 0
    ? data.value.items
    : []
})()

const relatedNewsItems = computed(() => {
  const _items = JSON.parse(JSON.stringify(relatedArticles))

  if (_isEmpty(_items)) {
    return {}
  }

  if (_items.length !== DEFAULT_LOAD_MORE_PER_PAGE) {
    _items.splice(-1)

    return {
      featured: {
        exist: false
      },
      total: _items.length,
      items: _items
    }
  }

  return {
    featured: {
      exist: false
    },
    total: _items.length,
    items: _items
  }
})

const sidebarRelatedArticles = RELATED_ARTICLES?.[vertical] || null

const verticalData = VERTICALS[vertical || VERTICAL.DEFAULT]
const guidesConfigData = GUIDES_CONFIG.config[vertical || VERTICAL.DEFAULT]
const isIncomeProtection = !!post?.isIncomeProtection
const isOvhc = !!post?.isOvhc

const layoutMaxWidth = (() => {
  return getKey(template, {
    default: 'lg:max-w-none',
    ArticleLightWithSidebar: 'lg:max-w-none',
    ArticleWithSidebarV2: 'lg:max-w-wrapper-sm',
    Article3Column: 'lg:max-w-wrapper-xs2'
  }) + ' w-full md:max-w-[42.0625rem] mx-auto'
})()

const authorUrl = computed(() => `${DEFAULT_AUTHOR_URL}${post?.author?.url}`)

const propsData = computed(() => {
  const postType = (() => {
    const type = POST_TYPES.find(item => item.type === post.postType)

    return {
      name: type?.name,
      url: type?.type !== 'Articles'
        ? `${verticalData.productPageUrl}${type?.urlSlug}/`
        : ''
    }
  })()

  const breadcrumbs = (() => {
    return [
      {
        text: 'Home',
        name: 'Home',
        url: '/'
      },
      {
        text: verticalData.guides_index_title,
        name: verticalData.guides_index_title,
        url: verticalData.productPageUrl
      },
      {
        text: postType.name,
        name: postType.name,
        url: postType.url
      },
      {
        text: (() => {
          if (devices.isMobile.value) {
            return post.title.slice(0, 32) + (post.title.length > 32 ? '...' : '')
          }

          return post.title
        })(),
        url: trailingSlash($route.path),
        name: post.title,
        active: true
      }
    ]
  })()

  const propsObj = {
    title: post.title,
    subTitle: post.subTitle,
    breadcrumbs,
    lastUpdate: post?.date ?? post.sys.publishedAt,
    featuredImage: GET_FEATURE_IMAGE(post.featureImage, props.template, devices.currBreakpoint.value),
    author: post.author,
    editor: post.editor,
    vertical,
    slug: post.slug,
    hasShareButtons: false,
    postType: post.postType,
    tags: (() => {
      const temp = !_isEmpty(post?.contentfulMetadata?.tags)
        ? post?.contentfulMetadata.tags
          .filter(tag => tag.name === 'Fact Checked')
          .map(tag => {
            return {
              name: tag.name
            }
          })
        : []

      return [
        ...temp,
        {
          name: verticalData.guides_index_title
        }
      ]
    })(),
    ctaText: guidesConfigData?.acticleCtaText
      ? guidesConfigData?.acticleCtaText
      : 'Get free quotes',
    layoutMaxWidth
  }

  return propsObj
})

const entriesMap = (() => {
  const map = new Map()

  if (_isEmpty(post)) {
    return map
  }

  const links = post.mainContent?.links

  for (const entry of links.entries.block) {
    map.set(entry.sys.id, entry)
  }

  return map
})()

const assetsMap = (() => {
  const map = new Map()

  if (_isEmpty(post)) {
    return map
  }

  const links = post.mainContent?.links

  for (const assets of links.assets.block) {
    map.set(assets.sys.id, assets)
  }

  return map
})()

const renderNodes = () => RENDER_NODES({
  entriesMap,
  assetsMap,
  vertical,
  template,
  internalDomainForInlinesHyperlink,
  author: post?.author,
  inlineComponents: {
    CTA: resolveComponent('AtomsCta'),
    CALCULATOR: resolveComponent('MoleculesCalculatorWithDisclaimer'),
    KeyPoints: resolveComponent('MoleculesKeyPoints'),
    InlineAge: resolveComponent('MoleculesInlineAge'),
    InlineCardCta: resolveComponent('V2MoleculesInlineCardCta'),
    InlineLifeStage: resolveComponent('MoleculesInlineLifeStage'),
    InlinePartner: resolveComponent('MoleculesInlinePartner'),
    VerticalFormWidget: resolveComponent('V2MoleculesVerticalFormWidgetSidebar'),

    MoleculesInlineLifeStage: resolveComponent('MoleculesInlineLifeStage'),
    Lifestage: resolveComponent('OrganismsInlineLifeStageAbTest'),
    State: resolveComponent('V2MoleculesInlineCardCta'),
    InlineAgeAbTest: resolveComponent('OrganismsInlineAgeAbTest'),
    AgeRange: resolveComponent('V2MoleculesInlineCardCta'),
    Partner: resolveComponent('MoleculesInlinePartner'),

    AUTHORBLOCK: resolveComponent('AtomsAuthorBlock'),
    FaqItem: resolveComponent('AtomsAccordion'),

    TextBox: resolveComponent('MoleculesHomeFooterInfo'),

    VideoAsset: resolveComponent('AtomsVideoFrame')
  }
})

const noCanonicalLink = !!post?.noCanonicalLink
const noIndex = props.isPreview
  ? false
  : !!post?.noIndex
const canonicalLink = trailingSlash(post?.canonicalLink || `${CC_DOMAIN}${post?.slug}/`)

const activeHeading = ref()

function handleScrollToJumplink (heading) {
  const id = strToKebabCase(heading)
  activeHeading.value = id

  const el = document.getElementById(id)

  if (el) {
    scrollToId(el)
  }
}

const PAGE_DETAILS = {
  title: post?.title || 'Compare Club',
  ogTitle: post?.seoTitle ?? post?.title ?? 'Compare Club',
  description: post?.metaDescription ?? '',
  ogUrl: canonicalLink,
  ogImage: propsData.value.featuredImage?.url || FEATURED_IMAGE_DEFAULT_IMAGE,
  ogType: PAGE_TYPES?.[post.postType] || ''
}

generateMeta({
  title: PAGE_DETAILS.title,
  ogTitle: PAGE_DETAILS.ogTitle,

  description: PAGE_DETAILS.description,
  ogDescription: PAGE_DETAILS.description,

  canonical: noCanonicalLink
    ? ''
    : PAGE_DETAILS.ogUrl,
  ogUrl: PAGE_DETAILS.ogUrl,
  ogImage: PAGE_DETAILS.ogImage,
  ogType: PAGE_DETAILS.ogType,
  meta: [
    {
      itemprop: 'mainEntityOfPage',
      content: canonicalLink
    },
    {
      itemprop: 'dateModified',
      content: post.sys.publishedAt
    }
  ],
  noIndex,
  noFollow: noIndex
})

const faqList = (() => {
  const list = !_isEmpty(post?.faqSchema) && !post.hideFaqSchema
    ? post?.faqSchema
    : undefined

  if (!list) {
    return []
  }

  return list?.map(item => ({
    title: item?.key ?? '',
    content: item?.value ?? ''
  }))
})()

useJsonld(() => {
  const _path = trailingSlash($route.path)

  const {
    vertical: routePage,
    slug: routeSlug
  } = $route.params

  const datePublished = post?.sys.firstPublishedAt
  const dateModified = typeof post?.date === 'undefined'
    ? post?.sys.publishedAt
    : post?.date

  const authorName = post?.author?.name

  return {
    '@context': 'https://schema.org',
    '@graph': [
      GENERATE_LOGO_SCHEMA({
        articleUrl: `${trailingSlash(`${CC_DOMAIN}${_path}`)}#logo`,
        imageUrl: post?.featureImage?.url || FEATURED_IMAGE_DEFAULT_IMAGE,
        caption: post?.featureImage?.description || 'Compare Club'
      }),
      GENERATE_WEBPAGE_SCHEMA({
        page: _path,
        pageTitle: PAGE_DETAILS?.ogTitle ?? PAGE_DETAILS.title,
        pageDescription: PAGE_DETAILS.description,
        aboutPath: `/${routePage}/#service`,
        mainEntityPath: `${_path}#article`,
        primaryImageOfPagePath: `${_path}#primaryimage`,
        breadcrumbPath: `${_path}#breadcrumb`,
        datePublished,
        dateModified
      }),
      GENERATE_ARTICLE_SCHEMA({
        page: routePage,
        slug: routeSlug,
        author: authorName,
        headline: PAGE_DETAILS.title,
        description: PAGE_DETAILS.description,
        audienceName: post?.audienceName ?? `Australian considering ${(routePage && routePage.replace(/-/g, ' ')) || 'insurance'}`,
        datePublished,
        dateModified
      }),
      GENERATE_PERSON_SCHEMA({
        page: authorUrl.value,
        author: authorName
      }),
      GENERATE_FAQ_SCHEMA({
        page: _path,
        faqs: faqList
      })
    ].filter(Boolean)
  }
})
</script>
